// @ts-nocheck

import Box from '@mui/material/Box';
import OutputSkeleton from '../../Skeleton/OutputSkeleton/OutputSkeleton';
import NotFoundPage from '../../NotFoundPage/NotFoundPage';
import PropTypes from 'prop-types';
import FontDataConfig from '../../GlobalSettings/Templates/FontDataConfig/FontDataConfig';
import { useState, useEffect, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { addBooking } from '../../../redux/slices/booking/booking';
import { getOutputTrip } from '../../../services/output/trip';
import {
  getTenantSettings,
  getTripSettings
} from '../../../services/tenant/tenant';
import { setHighlightColor } from '../../../redux/slices/color/color';
import { setHeadingFont, setBodyFont } from '../../../redux/slices/font/font';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  planIsSoldOut,
  planIsPastDeadline,
  getRemainingCapacity
} from '../../../utils/booking';
import { initGA, trackPageView } from '../../../utils/analytics';
import { getUserSession } from '../../../utils/session';
import {
  parseTripLogoSetting,
  withResolvedOperatorLogo
} from '../../../utils/operatorLogo';
import {
  parseContactSettings,
  withResolvedOperatorContact
} from '../../../utils/operatorContact';
import { getTripAssignedDiscountCodes } from '../../../utils/discountCodes';
import { resolveTemplateSettings } from '../../../utils/templateSettings';
import './TripOutput.css';

const SETTINGCODE = 'TEMPLATEFIELDS';
const GLOBAL_CONTACT_SETTINGCODE = 'GLOBALCONTACT';
const TRIP_LOGO_SETTINGCODE = 'TRIPLOGOURL';

// Dynamic import of components
const Crios = lazy(
  () =>
    import('../templates/Trips/DefaultOutput/DefaultTripOutput/DefaultTripOutput')
);
const Alekos = lazy(
  () =>
    import('../templates/Trips/SecondOutput/SecondTripOutput/SecondTripOutput')
);
const Nereus = lazy(
  () => import('../templates/Trips/ThirdOutput/ThirdTripOutput/ThirdTripOutput')
);
const Cherry = lazy(
  () =>
    import('../templates/Trips/CherryOutput/CherryTripOutput/CherryTripOutput')
);

const componentMap = {
  Crios,
  Alekos,
  Nereus,
  Cherry
};

/**
 * Functional React component for rendering a trip output configuration.
 *
 * @namespace Components
 *
 * @param {object} props - The properties of the component.
 * @param {string} props.id - The ID for the OutputDataConfig element.
 * @param {object} props.rest - Additional props to be spread onto parent element.
 *
 * @returns {JSX.Element} React element representing the OutputDataConfig component.
 */
const TripOutput = ({ id, ...rest }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['trip', 'common']);
  const { tripId } = useLoaderData();
  const {
    enableTripPlanVisibility,
    enableProposalTemplatePerTrip,
    enableTripCapacity,
    enableOperatorContactSettings,
    cherryServicesExpandedSetting
  } = useFlags();

  const [tripData, setTripData] = useState(null);
  const [isTripLoading, setIsTripLoading] = useState(true);
  const [didTripFetchSucceed, setDidTripFetchSucceed] = useState(true);
  const [selectedOutput, setSelectedOutput] = useState('Crios');
  const [showOperatorInfo, setShowOperatorInfo] = useState(true);
  const [cherryServicesExpanded, setCherryServicesExpanded] = useState(false);
  const [templateSettingsLoaded, setTemplateSettingsLoaded] = useState(false);
  const headingFont = useSelector((state) => state.font.headingFont);
  const bodyFont = useSelector((state) => state.font.bodyFont);
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = getUserSession();
  const isStaff = userData?.isStaff || false;

  useEffect(() => {
    const fetchOperatorContactSettings = async () => {
      if (!enableOperatorContactSettings) {
        return {};
      }

      const [tripContactResult, globalContactResult] = await Promise.allSettled(
        [
          getTripSettings(tripId, SETTINGCODE),
          getTenantSettings(GLOBAL_CONTACT_SETTINGCODE)
        ]
      );

      return {
        tripContactSettings:
          tripContactResult.status === 'fulfilled'
            ? parseContactSettings(tripContactResult.value?.data?.settingvalue)
            : {},
        globalContactSettings:
          globalContactResult.status === 'fulfilled'
            ? parseContactSettings(
                globalContactResult.value?.data?.settingvalue
              )
            : {}
      };
    };

    const fetchTripLogoUrl = async () => {
      if (!enableOperatorContactSettings) {
        return '';
      }

      try {
        const tripLogoSettings = await getTripSettings(
          tripId,
          TRIP_LOGO_SETTINGCODE
        );

        return parseTripLogoSetting(tripLogoSettings?.data?.settingvalue);
      } catch (error) {
        return '';
      }
    };

    const fetchData = async () => {
      try {
        const { data } = await getOutputTrip(tripId);

        if (data && Object.keys(data).length > 0) {
          const [operatorContactSettings, tripLogoUrl] = await Promise.all([
            fetchOperatorContactSettings(),
            fetchTripLogoUrl()
          ]);
          const contactResolvedData = withResolvedOperatorContact(data, {
            ...operatorContactSettings,
            enabled: enableOperatorContactSettings
          });
          const resolvedData = withResolvedOperatorLogo(contactResolvedData, {
            tripLogoUrl,
            enabled: enableOperatorContactSettings
          });
          const {
            basecurrency,
            sellingcurrency,
            tenantdomain,
            tenantname,
            tenantlogo,
            tenantid,
            googleanalyticsid
          } = resolvedData?.tenant || {};

          setTripData(resolvedData);
          setDidTripFetchSucceed(true);

          // Update page title
          const tripName = resolvedData?.tripname;
          if (tripName && tenantname) {
            document.title = `${tripName} - ${tenantname}`;
          }

          // Initialize GA and track page view if GA ID is configured
          if (googleanalyticsid) {
            const currency =
              sellingcurrency?.currencycode || basecurrency?.currencycode;
            initGA(googleanalyticsid);
            trackPageView(`/itineraries/${resolvedData?.tripid}`, {
              page_title:
                tripName && tenantname
                  ? `${tripName} - ${tenantname}`
                  : undefined,
              trip_id: resolvedData?.tripid,
              trip_name: resolvedData?.tripname,
              currency
            });
          }

          const tenantData = {
            domain: tenantdomain,
            name: tenantname,
            logo: tenantlogo,
            tenantid: tenantid,
            basecurrency,
            sellingcurrency
          };

          const serializedTenantData = JSON.stringify(tenantData);

          //This serves as a fallback when tenant data is not present in local storage; route middleware is expected to set it
          if (!localStorage.getItem('tenantData')) {
            localStorage.setItem('tenantData', serializedTenantData);
          }

          const tripCapacity = resolvedData?.capacity ?? null;
          const usedCapacity = resolvedData?.usedCapacity ?? 0;
          const bookingplans = sortTripPlans(resolvedData);

          // Trip capacity calculations
          const remainingCapacity =
            enableTripCapacity && tripCapacity !== null
              ? getRemainingCapacity(tripCapacity, bookingplans, usedCapacity)
              : null;

          const isFullCapacity =
            enableTripCapacity &&
            remainingCapacity !== null &&
            remainingCapacity === 0;

          dispatch(
            addBooking({
              tenant: resolvedData?.tenant,
              bookingplans: sortTripPlans(resolvedData),
              addons: resolvedData?.tripaddon ?? [],
              trip: {
                tripname: resolvedData?.tripname,
                tripcontent: resolvedData?.tripcontent,
                startdate: resolvedData?.startdate,
                duration: resolvedData?.duration,
                tripid: resolvedData?.tripid,
                tripkey: resolvedData?.tripkey,
                fees: resolvedData?.fees ?? [],
                capacity: resolvedData?.capacity ?? null,
                requiresSignedDocument:
                  resolvedData?.tripsignabledocument?.[0]?.required ?? false,
                signedDocumentTemplateId:
                  resolvedData?.tripsignabledocument?.[0]?.signabledocument
                    ?.templateid ?? null,
                operatorEmail: resolvedData?.operatorContact?.email,
                operatorContact: resolvedData?.operatorContact,
                paymentplan: resolvedData?.paymentplan,
                discountCodes: getTripAssignedDiscountCodes(resolvedData)
              },
              usedCapacity: resolvedData?.usedCapacity ?? 0,
              isFullCapacity
            })
          );
        } else {
          setDidTripFetchSucceed(false);
        }
      } catch (error) {
        setDidTripFetchSucceed(false);
      } finally {
        setIsTripLoading(false);
      }
    };

    const fetchTemplateSettings = async () => {
      try {
        const globalSettings = await getTenantSettings(SETTINGCODE);

        const tripSettings = !enableProposalTemplatePerTrip
          ? null
          : await getTripSettings(tripId, SETTINGCODE);

        const resolved = resolveTemplateSettings({
          globalSettingValue: globalSettings?.data?.settingvalue,
          tripSettingValue: tripSettings?.data?.settingvalue,
          useTripTemplate: enableProposalTemplatePerTrip
        });

        dispatch(setHeadingFont(resolved.headingfont));
        dispatch(setBodyFont(resolved.bodyfont));
        dispatch(setHighlightColor(resolved.color));
        setSelectedOutput(resolved.name);
        setShowOperatorInfo(resolved.showOperatorInfo);
        setCherryServicesExpanded(resolved.cherryServicesExpanded);
      } catch (error) {
        setCherryServicesExpanded(false);
        console.error(t('common:errorLoadingSettings'), error);
      } finally {
        setTemplateSettingsLoaded(true);
      }
    };

    //Wait for feature flag to initialize
    if (
      enableTripPlanVisibility !== undefined &&
      enableProposalTemplatePerTrip !== undefined &&
      enableOperatorContactSettings !== undefined
    ) {
      setTemplateSettingsLoaded(false);
      fetchData();
      fetchTemplateSettings();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tripId,
    dispatch,
    t,
    enableTripPlanVisibility,
    enableProposalTemplatePerTrip,
    enableOperatorContactSettings
  ]);

  const getVisibilityStatus = (pricingPlan = {}, tripdData = {}) => {
    const { capacity: tripCapacity, usedCapacity } = tripdData;
    const {
      ishiddenfrompassengers,
      autohidewhensoldout,
      autohideafterdeadline
    } = pricingPlan;

    if (!isStaff) {
      if (enableTripPlanVisibility) {
        // Manual hide takes priority
        if (ishiddenfrompassengers) {
          return { hidden: true, reason: 'manual', label: t('common:hidden') };
        }

        // Check sold out condition
        if (autohidewhensoldout) {
          // Check plan-level sold out
          const planSoldOut = planIsSoldOut(pricingPlan);

          // Check trip-level sold out (if trip capacity is set)
          const tripSoldOut =
            tripCapacity !== null &&
            tripCapacity !== undefined &&
            tripCapacity - usedCapacity <= 0;

          if (planSoldOut || tripSoldOut) {
            return {
              hidden: true,
              reason: 'soldout',
              label: t('trippricing:soldOut')
            };
          }
        }

        // Check deadline condition
        if (autohideafterdeadline) {
          if (planIsPastDeadline(pricingPlan)) {
            return {
              hidden: true,
              reason: 'deadline',
              label: t('trippricing:bookingDeadlinePast')
            };
          }
        }
      }
    }

    return { hidden: false, reason: null, label: null };
  };

  const sortTripPlans = (tripData) => {
    const tripPlans = tripData?.tripplan || [];

    if (Array.isArray(tripPlans)) {
      const filteredTripPlans = tripPlans?.filter((plan) => {
        const { hidden } = getVisibilityStatus(plan, tripData);

        return !hidden;
      });

      const sortedTripPlans = filteredTripPlans?.sort((a, b) => {
        return Number(a?.seq) - Number(b?.seq);
      });

      return sortedTripPlans.map((tripPlan) => {
        return {
          ...tripPlan,
          selectedQuantity: 0,
          depositamount: Number(tripPlan?.depositamount),
          maxavailable: Number(tripPlan?.maxavailable),
          priceperperson: Number(tripPlan?.priceperperson),
          isSelected: false
        };
      });
    }

    return [];
  };

  // Determines whether passenger plans should be hidden (for external users) - See XGE-2730
  // We want to hide plans from external users if there are no plans or if all plans are marked as "Hidden from passengers"
  // Rules changed on ticket XGE-2963 - now we hide plans if they are all marked as "Hidden from passengers", regardless of the user session.
  // Rules changed again on ticket XGE-3130 - operators can always see all plans, regardless of the "Hidden from passengers" setting. This is to allow operators to manage their trips and bookings without restrictions.
  const shouldHidePlansFromPassengers = (
    tripDataValue,
    isStaffValue,
    visibilityEnabled
  ) => {
    if (!tripDataValue || isStaffValue) return false;

    const tripPlans = Array.isArray(tripDataValue?.tripplan)
      ? tripDataValue.tripplan
      : [];

    if (tripPlans.length === 0) return true;
    if (!visibilityEnabled) return false;

    return tripPlans.every((plan) => plan?.ishiddenfrompassengers);
  };

  const hidePlansFromPassengers = shouldHidePlansFromPassengers(
    tripData,
    isStaff,
    enableTripPlanVisibility
  );

  // Default to Crios if we don't recognize the configured output template in the tenant's settings
  const SelectedComponent =
    componentMap[selectedOutput] || componentMap['Crios'];

  if (!didTripFetchSucceed) {
    return <NotFoundPage id='not-found-output' />;
  }

  return (
    <Box
      data-testid='trip-output'
      id={id}
      {...rest}
      className='output__container'
    >
      <FontDataConfig
        fonts={[
          { family: headingFont, weights: [400, 700] },
          { family: bodyFont, weights: [400, 700] }
        ]}
      >
        {() => (
          <div>
            {isTripLoading ||
            (cherryServicesExpandedSetting && !templateSettingsLoaded) ? (
              <OutputSkeleton id='output-skeleton' />
            ) : (
              SelectedComponent &&
              tripData && (
                <div className='template-output-component-container'>
                  <SelectedComponent
                    id={`${selectedOutput.toLowerCase()}-template`}
                    data={tripData}
                    showOperatorInfo={showOperatorInfo}
                    isStaffUser={isStaff}
                    hidePlansFromPassengers={hidePlansFromPassengers}
                    {...(selectedOutput === 'Cherry' && {
                      expandServicesOnLoad: Boolean(
                        cherryServicesExpandedSetting &&
                        cherryServicesExpanded &&
                        !searchParams.get('printpdf')
                      )
                    })}
                  />
                </div>
              )
            )}
          </div>
        )}
      </FontDataConfig>
    </Box>
  );
};

TripOutput.propTypes = {
  id: PropTypes.string.isRequired
};

export default TripOutput;
